import React, { useEffect, useState } from "react";
import { EyeIcon, EyeSlashIcon } from "../../lib/icons";
import { IndofastLogo } from "../layout/IndofastLogo";

function PhoneRingIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
      <path d="M6.4 4.5h2L9.4 7a1.1 1.1 0 0 1-.2 1.12l-.83.92a10.5 10.5 0 0 0 2.57 2.57l.92-.83c.34-.3.82-.38 1.23-.2l2.39 1.01v2c0 .63-.5 1.14-1.13 1.13A10.76 10.76 0 0 1 4.5 5.63c0-.63.51-1.13 1.14-1.13Z" />
    </svg>
  );
}
function QueueIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="14" height="3.5" rx="1.5" />
      <rect x="3" y="9" width="14" height="3.5" rx="1.5" />
      <rect x="3" y="14" width="9" height="3" rx="1.5" />
    </svg>
  );
}
function DashboardChartIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="14" height="14" rx="2.5" />
      <path d="M7 13.5V10M10 13.5V7.5M13 13.5V11" />
    </svg>
  );
}

function SwapIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7h14M13 4l4 3-4 3" />
      <path d="M17 13H3M7 10l-4 3 4 3" />
    </svg>
  );
}
function DriveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="8" width="16" height="7" rx="2" />
      <path d="M5 8l2-4h6l2 4" />
      <circle cx="6" cy="15" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="14" cy="15" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}
function RepeatIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12a6 6 0 0 0 10.2 2.8M16 8A6 6 0 0 0 5.8 5.2" />
      <path d="M14.2 15.2l2-2.4-2.4-1.6" />
      <path d="M5.8 4.8L3.8 7.2l2.4 1.6" />
    </svg>
  );
}

const STEPS = [
  { word: "SWAP",   icon: <SwapIcon /> },
  { word: "DRIVE",  icon: <DriveIcon /> },
  { word: "REPEAT", icon: <RepeatIcon /> },
];

function LoginPage({ error, loggingIn, loginForm, onChange, onSubmit }) {
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % 3), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="login-page">

      {/* ── Left hero panel ── */}
      <section className="login-hero">
        <div className="login-brand">
          <IndofastLogo height={44} variant="white" />
        </div>

        <div className="hero-copy">
          <p className="login-kicker-hero">Support CRM</p>
          <h1>Turn every customer call into a tracked ticket.</h1>
          <p>Log calls, raise tickets, and resolve issues without switching between tools.</p>
        </div>

        <div className="login-feature-grid">
          <article className="login-feature-card">
            <span className="login-feature-icon"><PhoneRingIcon /></span>
            <div>
              <strong>Call intake &amp; tickets</strong>
              <p>Capture inbound calls and open a ticket in one step.</p>
            </div>
          </article>
          <article className="login-feature-card">
            <span className="login-feature-icon"><QueueIcon /></span>
            <div>
              <strong>Live ticket queue</strong>
              <p>Work open, in progress, and pending issues in order.</p>
            </div>
          </article>
          <article className="login-feature-card">
            <span className="login-feature-icon"><DashboardChartIcon /></span>
            <div>
              <strong>Resolution dashboard</strong>
              <p>Track volume, categories, regions, and agent performance.</p>
            </div>
          </article>
        </div>

        <div className="login-tagline">
          {STEPS.map(({ word }, i) => (
            <React.Fragment key={word}>
              <span className={`tagline-word${i === step ? " tagline-word--active" : ""}`}>
                {word}
              </span>
              {i < STEPS.length - 1 && <span className="tagline-sep">+</span>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── Right sign-in panel ── */}
      <section className="login-panel">
        <div className="login-panel-inner">

          <p className="login-kicker-panel">
            <span className="kicker-icon">{STEPS[step].icon}</span>
            Sign In
          </p>

          <h2>Access your CRM workspace</h2>
          <p className="login-copy">Use your backend account credentials to start the session.</p>

          <form className="login-form" onSubmit={onSubmit}>

            <label className="field-stack">
              <span>Username</span>
              <input
                value={loginForm.username}
                onChange={(e) => onChange("username", e.target.value)}
                placeholder="Enter username"
                required
                autoComplete="username"
              />
            </label>

            <label className="field-stack">
              <span>Password</span>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={loginForm.password}
                  onChange={(e) => onChange("password", e.target.value)}
                  placeholder="Enter password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>
            </label>

            <div className="login-form-row">
              <label className="keep-signed-in">
                <input type="checkbox" />
                <span>Keep me signed in</span>
              </label>
              <button type="button" className="forgot-link">Forgot password?</button>
            </div>

            {error ? <p className="message-banner error">{error}</p> : null}

            <button className="primary-login-button" disabled={loggingIn} type="submit">
              {loggingIn ? "Signing in…" : "Login to Indofast"}
            </button>

            <p className="login-secure-note">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: 14, height: 14, flexShrink: 0, color: "#22C55E" }}>
                <rect x="3" y="7" width="10" height="7" rx="1.5" />
                <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
              </svg>
              Encrypted session · Internal access only
            </p>

          </form>
        </div>
      </section>

    </div>
  );
}

export default LoginPage;
